import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { Check, Upload, X, FileText, ArrowLeft, ArrowRight, CheckCircle2, KeyRound, Copy, ShieldAlert, AlertCircle, MessageCircle, LogIn } from "lucide-react";
import { toast, Toaster } from "sonner";
import { api, waLink, getErr, safeCopy } from "../lib/api";
import { getServiceMeta } from "../lib/serviceIcons";

const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Delhi","Jammu and Kashmir","Ladakh","Chandigarh","Puducherry","Andaman and Nicobar Islands","Dadra and Nagar Haveli and Daman and Diu","Lakshadweep"
];

const STEPS = ["Service", "Documents", "Your details", "Done"];

export default function Apply() {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [step, setStep] = useState(1);
  const [uploads, setUploads] = useState([]); // [{id, filename, size, label}]
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    full_name: "", mobile: "", whatsapp: "", email: "",
    pan: "", aadhaar: "",
    state: "", district: "", city: "", address: "", pincode: "", remarks: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [adminWa, setAdminWa] = useState("7008144614");

  useEffect(() => {
    api.get("/config/public").then((r) => r.data?.admin_whatsapp && setAdminWa(r.data.admin_whatsapp)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/services/${serviceId}`)
      .then((r) => setService(r.data))
      .catch(() => navigate("/services"));
  }, [serviceId, navigate]);

  const onDrop = useCallback(async (acceptedFiles) => {
    setUploading(true);
    try {
      for (const f of acceptedFiles) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setUploads((prev) => [...prev, { ...data, label: f.name }]);
      }
      toast.success(`${acceptedFiles.length} file(s) uploaded`);
    } catch (e) {
      toast.error(getErr(e, "Upload failed"));
    } finally {
      setUploading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/jpeg": [], "image/png": [], "image/webp": [], "application/pdf": [] },
    maxSize: 10 * 1024 * 1024,
  });

  const removeUpload = (id) => setUploads((p) => p.filter((u) => u.id !== id));

  const copyToClipboard = async (text, label) => {
    if (!text) return;
    const ok = await safeCopy(text);
    if (ok) toast.success(`${label} copied`);
    else toast.error(`Couldn't copy automatically — please select and copy manually.`);
  };

  const validateForm = () => {
    const required = ["full_name", "mobile", "email", "state", "district", "city", "address", "pincode"];
    for (const k of required) {
      if (!form[k].trim()) {
        toast.error(`Please enter ${k.replace("_", " ")}`);
        return false;
      }
    }
    if (!/^[6-9]\d{9}$/.test(form.mobile)) {
      toast.error("Enter a valid 10-digit Indian mobile number");
      return false;
    }
    if (!/^\d{6}$/.test(form.pincode)) {
      toast.error("Enter a valid 6-digit PIN code");
      return false;
    }
    return true;
  };

  const submit = async () => {
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        whatsapp: form.whatsapp || form.mobile,
        service_id: serviceId,
        document_ids: uploads.map((u) => u.id),
      };
      const { data } = await api.post("/leads", payload);
      setConfirmation(data);
      setStep(4);
    } catch (e) {
      toast.error(getErr(e, "Submission failed. Please check the form fields."));
    } finally {
      setSubmitting(false);
    }
  };

  if (!service) return <div className="container-pad py-20 text-center text-[#5C6661]">Loading service…</div>;

  return (
    <div className="container-pad mx-auto max-w-[1100px] py-12 md:py-20" data-testid="apply-page">
      <Toaster richColors position="top-right" />

      <Link to="/services" className="text-sm text-[#5C6661] hover:text-[#121C17] inline-flex items-center gap-1 mb-6" data-testid="apply-back">
        <ArrowLeft size={14} /> Back to services
      </Link>

      <div className="grid lg:grid-cols-12 gap-10">
        {/* SIDEBAR */}
        <aside className="lg:col-span-4">
          <div className="bento-card p-7 sticky top-24">
            {(() => {
              const { icon: Icon, accent, bgSolid } = getServiceMeta(service);
              return (
                <div className="mb-4 w-14 h-14 rounded-2xl grid place-items-center" style={{ background: bgSolid, color: accent }}>
                  <Icon size={26} />
                </div>
              );
            })()}
            <span className="label-eyebrow text-[10px]">{service.category}</span>
            <h2 className="mt-3 text-2xl font-display font-medium text-[#121C17]">{service.name}</h2>
            <div className="mt-2 text-2xl font-display font-medium" style={{ color: getServiceMeta(service).accent }}>{service.price_label}</div>
            <p className="mt-3 text-sm text-[#5C6661]">{service.description}</p>

            <div className="mt-6 pt-6 border-t border-[#E5E2DC]">
              <h4 className="label-eyebrow mb-3">Required documents</h4>
              <ul className="space-y-2" data-testid="required-docs-list">
                {service.documents.map((d) => (
                  <li key={d} className="flex items-start gap-2 text-sm text-[#121C17]">
                    <Check size={14} className="text-[#1E4D3B] mt-1 shrink-0" /> {d}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </aside>

        {/* MAIN */}
        <div className="lg:col-span-8">
          {/* Stepper */}
          <ol className="flex items-center gap-2 mb-10" data-testid="apply-stepper">
            {STEPS.map((label, i) => {
              const n = i + 1;
              const done = step > n;
              const active = step === n;
              return (
                <li key={label} className="flex items-center gap-2 flex-1">
                  <span className={`w-7 h-7 rounded-full grid place-items-center text-xs font-medium ${
                    done ? "bg-[#1E4D3B] text-white" : active ? "bg-[#121C17] text-white" : "bg-[#F3F1EC] text-[#5C6661]"
                  }`}>
                    {done ? <Check size={14} /> : n}
                  </span>
                  <span className={`text-xs ${active ? "text-[#121C17] font-medium" : "text-[#5C6661]"} hidden sm:inline`}>{label}</span>
                  {n < STEPS.length && <span className="flex-1 h-px bg-[#E5E2DC]" />}
                </li>
              );
            })}
          </ol>

          {/* Step content */}
          {step === 1 && (
            <div data-testid="step-service">
              <h3 className="text-2xl font-display font-medium text-[#121C17]">You've selected {service.name}</h3>
              <p className="mt-3 text-[#5C6661]">Please keep the documents on the left handy. PDF, JPG or PNG files work — up to 10MB each.</p>
              <button onClick={() => setStep(2)} data-testid="step-service-next"
                className="mt-8 btn-primary rounded-full px-6 py-3 text-sm font-medium inline-flex items-center gap-2">
                Continue <ArrowRight size={14} />
              </button>
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-documents">
              <h3 className="text-2xl font-display font-medium text-[#121C17]">Upload your documents</h3>
              <p className="mt-3 text-[#5C6661]">Drag-and-drop or tap to pick — your phone camera works too.</p>

              <div {...getRootProps()} data-testid="dropzone"
                className={`mt-6 border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-colors ${
                  isDragActive ? "border-[#1E4D3B] bg-[#F3F1EC]" : "border-[#E5E2DC] bg-white hover:border-[#1E4D3B]"
                }`}>
                <input {...getInputProps()} data-testid="dropzone-input" />
                <Upload size={32} className="mx-auto text-[#1E4D3B]" />
                <p className="mt-4 font-medium text-[#121C17]">{isDragActive ? "Drop files here" : "Click or drag files to upload"}</p>
                <p className="text-xs text-[#5C6661] mt-1">PDF · JPG · PNG · WebP · up to 10MB each</p>
              </div>

              {uploading && <p className="mt-3 text-sm text-[#5C6661]" data-testid="upload-progress">Uploading…</p>}

              {uploads.length > 0 && (
                <ul className="mt-6 space-y-2" data-testid="upload-list">
                  {uploads.map((u) => (
                    <li key={u.id} className="bento-card p-4 flex items-center gap-3">
                      <FileText size={18} className="text-[#1E4D3B]" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-[#121C17] truncate">{u.filename}</div>
                        <div className="text-xs text-[#5C6661]">{(u.size / 1024).toFixed(1)} KB</div>
                      </div>
                      <button onClick={() => removeUpload(u.id)} data-testid={`remove-upload-${u.id}`}
                        className="text-[#5C6661] hover:text-red-600 p-1"><X size={16} /></button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-8 flex justify-between gap-3">
                <button onClick={() => setStep(1)} data-testid="step-documents-back"
                  className="rounded-full px-6 py-3 text-sm font-medium border border-[#E5E2DC] hover:bg-[#F3F1EC]">Back</button>
                <button onClick={() => setStep(3)} data-testid="step-documents-next"
                  className="btn-primary rounded-full px-6 py-3 text-sm font-medium inline-flex items-center gap-2">
                  Continue <ArrowRight size={14} />
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-details">
              <h3 className="text-2xl font-display font-medium text-[#121C17]">Your details</h3>
              <p className="mt-3 text-[#5C6661]">We'll WhatsApp you within 10 minutes after submission.</p>

              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <Field label="Full Name *" v={form.full_name} on={(v) => setForm({ ...form, full_name: v })} test="form-full_name" />
                <Field label="Mobile Number *" v={form.mobile} on={(v) => setForm({ ...form, mobile: v })} test="form-mobile" />
                <Field label="WhatsApp Number" v={form.whatsapp} on={(v) => setForm({ ...form, whatsapp: v })} test="form-whatsapp" placeholder="Same as mobile if blank" />
                <Field label="Email *" v={form.email} on={(v) => setForm({ ...form, email: v })} test="form-email" type="email" />
                <Field label="PAN Number" v={form.pan} on={(v) => setForm({ ...form, pan: v.toUpperCase() })} test="form-pan" placeholder="ABCDE1234F (optional)" />
                <Field label="Aadhaar Number" v={form.aadhaar} on={(v) => setForm({ ...form, aadhaar: v })} test="form-aadhaar" placeholder="12-digit (optional)" />
                <div>
                  <label className="text-xs label-eyebrow">State *</label>
                  <select value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="form-state"
                    className="mt-2 w-full bg-white border border-[#E5E2DC] rounded-xl px-4 py-3 text-sm focus:border-[#1E4D3B]">
                    <option value="">Select state</option>
                    {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <Field label="District *" v={form.district} on={(v) => setForm({ ...form, district: v })} test="form-district" />
                <Field label="Town / City *" v={form.city} on={(v) => setForm({ ...form, city: v })} test="form-city" />
                <Field label="PIN Code *" v={form.pincode} on={(v) => setForm({ ...form, pincode: v })} test="form-pincode" />
                <div className="sm:col-span-2">
                  <label className="text-xs label-eyebrow">Full Address *</label>
                  <textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="form-address"
                    rows={3} className="mt-2 w-full bg-white border border-[#E5E2DC] rounded-xl px-4 py-3 text-sm focus:border-[#1E4D3B]" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs label-eyebrow">Remarks</label>
                  <textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} data-testid="form-remarks"
                    rows={2} placeholder="Anything else we should know?" className="mt-2 w-full bg-white border border-[#E5E2DC] rounded-xl px-4 py-3 text-sm focus:border-[#1E4D3B]" />
                </div>
              </div>

              <div className="mt-8 flex justify-between gap-3">
                <button onClick={() => setStep(2)} data-testid="step-details-back"
                  className="rounded-full px-6 py-3 text-sm font-medium border border-[#E5E2DC] hover:bg-[#F3F1EC]">Back</button>
                <button onClick={submit} disabled={submitting} data-testid="step-details-submit"
                  className="btn-primary rounded-full px-6 py-3 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60">
                  {submitting ? "Submitting…" : <>Submit Lead <ArrowRight size={14} /></>}
                </button>
              </div>
            </div>
          )}

          {step === 4 && confirmation && (
            <div data-testid="step-done" className="bento-card p-6 sm:p-10">
              {confirmation.existing_account ? (
                <ExistingAccountPanel
                  confirmation={confirmation}
                  applicantName={form.full_name}
                  applicantMobile={form.mobile}
                  serviceName={service?.name}
                  adminWa={adminWa}
                />
              ) : (
              <>
              <div className="text-center">
                <CheckCircle2 size={48} className="mx-auto text-[#1E4D3B]" />
                <h3 className="mt-4 text-2xl sm:text-3xl font-display font-medium text-[#121C17]">You're in! 🎉</h3>
                <p className="mt-2 text-[#5C6661]">
                  Your reference number is <span className="font-mono text-[#121C17] font-semibold" data-testid="confirmation-ref">{confirmation.ref}</span>.
                  Our team will reach you within 10 minutes on WhatsApp.
                </p>
              </div>

              {/* Credentials card */}
              {confirmation.credentials && (
                <div className="mt-8 rounded-2xl border-2 border-[#1E4D3B] bg-gradient-to-br from-[#E4EDE8] to-[#FDFCFB] p-5 sm:p-6" data-testid="confirmation-credentials">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#1E4D3B] text-white grid place-items-center shrink-0">
                      <KeyRound size={18} />
                    </div>
                    <div>
                      <h4 className="font-display font-semibold text-lg text-[#121C17]">Your portal login is ready</h4>
                      <p className="text-xs text-[#5C6661] mt-0.5">Save these now — the password is shown only once. Use them at <Link to="/login" className="underline text-[#1E4D3B] font-medium">/login</Link> to track your application anytime.</p>
                    </div>
                  </div>

                  <div className="mt-5 grid sm:grid-cols-2 gap-3">
                    <div className="bg-white rounded-xl border border-[#E5E2DC] p-4">
                      <div className="text-[10px] uppercase tracking-wider text-[#5C6661]">Login ID (Mobile)</div>
                      <div className="mt-1.5 flex items-center gap-2">
                        <span className="font-mono text-lg text-[#121C17]" data-testid="confirmation-login-id">{confirmation.credentials.login_id}</span>
                        <button onClick={() => copyToClipboard(confirmation.credentials.login_id, "Login ID")} data-testid="copy-login-id"
                          className="ml-auto rounded-full p-2 hover:bg-[#F3F1EC]" aria-label="Copy login id"><Copy size={14} className="text-[#5C6661]" /></button>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl border border-[#E5E2DC] p-4">
                      <div className="text-[10px] uppercase tracking-wider text-[#5C6661]">Password (one-time)</div>
                      <div className="mt-1.5 flex items-center gap-2">
                        <span className="font-mono text-lg text-[#D17B5D] font-semibold tracking-wider" data-testid="confirmation-password">{confirmation.credentials.password}</span>
                        <button onClick={() => copyToClipboard(confirmation.credentials.password, "Password")} data-testid="copy-password"
                          className="ml-auto rounded-full p-2 hover:bg-[#F3F1EC]" aria-label="Copy password"><Copy size={14} className="text-[#5C6661]" /></button>
                      </div>
                    </div>
                  </div>

                  <button onClick={() => copyToClipboard(
                      `FinSahayak login\nReference: ${confirmation.ref}\nLogin ID: ${confirmation.credentials.login_id}\nPassword: ${confirmation.credentials.password}\nPortal: ${window.location.origin}/login`,
                      "All credentials"
                    )} data-testid="copy-all-creds"
                    className="mt-4 w-full sm:w-auto rounded-full px-5 py-2.5 text-sm font-medium border border-[#1E4D3B] text-[#1E4D3B] hover:bg-[#1E4D3B] hover:text-white inline-flex items-center justify-center gap-1.5 transition-colors">
                    <Copy size={14} /> Copy all credentials
                  </button>
                  <p className="mt-3 text-[11px] text-[#5C6661] flex items-start gap-1.5">
                    <ShieldAlert size={12} className="mt-0.5 shrink-0 text-amber-600" />
                    For your security, this password won't be shown again. Note it down or take a screenshot now.
                  </p>
                </div>
              )}

              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <Link to="/login" data-testid="confirmation-login-cta"
                  className="btn-primary rounded-full px-6 py-3 text-sm font-medium">Go to login</Link>
                <a href={waLink(`Hi, I just submitted a request. My reference is ${confirmation.ref}.`)} target="_blank" rel="noopener noreferrer" data-testid="confirmation-whatsapp"
                  className="rounded-full px-6 py-3 text-sm font-medium text-white" style={{ background: "#25D366" }}>Open WhatsApp</a>
                <Link to="/" data-testid="confirmation-home"
                  className="rounded-full px-6 py-3 text-sm font-medium border border-[#E5E2DC] hover:bg-[#F3F1EC]">Back to home</Link>
              </div>
              </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, v, on, test, type = "text", placeholder }) => (
  <div>
    <label className="text-xs label-eyebrow">{label}</label>
    <input type={type} value={v} onChange={(e) => on(e.target.value)} placeholder={placeholder} data-testid={test}
      className="mt-2 w-full bg-white border border-[#E5E2DC] rounded-xl px-4 py-3 text-sm focus:border-[#1E4D3B]" />
  </div>
);


function ExistingAccountPanel({ confirmation, applicantName, applicantMobile, serviceName, adminWa }) {
  const matchedHuman = (confirmation.matched || []).map((m) => ({
    mobile: "Mobile number",
    pan: "PAN",
    aadhaar: "Aadhaar",
  }[m] || m)).join(", ");

  const waNumber = (() => {
    const m = (adminWa || "7008144614").replace(/\D/g, "");
    return m.length === 10 ? `91${m}` : m;
  })();

  const helpMessage = [
    "Hi FinSahayak, I need help logging into my portal.",
    "",
    `*Name:* ${applicantName || "—"}`,
    `*Mobile:* +91 ${applicantMobile || "—"}`,
    `*Service of interest:* ${serviceName || "—"}`,
    confirmation?.ref ? `*Existing reference:* ${confirmation.ref}` : null,
    confirmation?.login_id_hint ? `*Account hint:* ${confirmation.login_id_hint}` : null,
    "",
    "Could you please help me with my login ID and password?",
    "",
    "(Submitted via finsahayak.com)",
  ].filter(Boolean).join("\n");

  const waHref = `https://wa.me/${waNumber}?text=${encodeURIComponent(helpMessage)}`;

  return (
    <div data-testid="existing-account-panel">
      <div className="text-center">
        <div className="w-14 h-14 rounded-full mx-auto bg-amber-100 grid place-items-center text-amber-700">
          <AlertCircle size={28} />
        </div>
        <h3 className="mt-4 text-2xl sm:text-3xl font-display font-medium text-[#121C17]">You already have an account</h3>
        <p className="mt-2 text-[#5C6661] max-w-xl mx-auto">
          {confirmation.message || "An account already exists with these details. Please log in with your existing login ID and password."}
        </p>
      </div>

      <div className="mt-7 rounded-2xl border-2 border-amber-300 bg-amber-50/40 p-5 sm:p-6 max-w-xl mx-auto" data-testid="existing-account-card">
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          {confirmation.ref && (
            <div className="bg-white rounded-xl border border-[#E5E2DC] p-3.5">
              <div className="text-[10px] uppercase tracking-wider text-[#5C6661]">Existing reference</div>
              <div className="mt-1 font-mono text-[#121C17]" data-testid="existing-account-ref">{confirmation.ref}</div>
            </div>
          )}
          {confirmation.login_id_hint && (
            <div className="bg-white rounded-xl border border-[#E5E2DC] p-3.5">
              <div className="text-[10px] uppercase tracking-wider text-[#5C6661]">Your login ID hint</div>
              <div className="mt-1 font-mono text-[#121C17]" data-testid="existing-account-hint">{confirmation.login_id_hint}</div>
            </div>
          )}
        </div>
        {matchedHuman && (
          <p className="mt-3 text-xs text-[#5C6661]">Matched on: <strong className="text-[#121C17]">{matchedHuman}</strong></p>
        )}
        <p className="mt-4 text-xs text-[#5C6661]">
          For your security, we don't show your password again here. Please use the password you saved when you first registered.
          If you've forgotten it, message us on WhatsApp and we'll help you reset it.
        </p>
      </div>

      <div className="mt-7 flex flex-wrap justify-center gap-3">
        <Link to="/login" data-testid="existing-account-login-cta"
          className="btn-primary rounded-full px-6 py-3 text-sm font-medium inline-flex items-center gap-2">
          <LogIn size={14} /> Go to login
        </Link>
        <a href={waHref} target="_blank" rel="noopener noreferrer" data-testid="existing-account-login-help"
          className="rounded-full px-6 py-3 text-sm font-medium text-white inline-flex items-center gap-2" style={{ background: "#25D366" }}>
          <MessageCircle size={14} /> Login help on WhatsApp
        </a>
        <Link to="/" data-testid="existing-account-home"
          className="rounded-full px-6 py-3 text-sm font-medium border border-[#E5E2DC] hover:bg-[#F3F1EC]">Back to home</Link>
      </div>
    </div>
  );
}
